import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { collection, getDocs, doc, getDoc, updateDoc, addDoc, setDoc, Timestamp } from 'firebase/firestore'
import { useSelector } from 'react-redux'

import { useAuthUser } from './useUser'
import useOrganizationAccess from './useOrganizationAccess'

import { db } from '@/firebase'

// Query keys
export const CREDITS_QUERY_KEY = 'credits'

export const USER_CREDITS_QUERY_KEY = 'user-credits'

/**
 * Returns the same calendar date in the next month, clamped to the last day
 * of that month if the target month is shorter (e.g. Jan‑31 → Feb‑28/29).
 * @param {Timestamp} timestamp
 * @returns {Timestamp}
 */
const addOneMonth = timestamp => {
  const date = timestamp.toDate()
  const startMonth = date.getMonth()

  // Naïvely add one month
  date.setMonth(startMonth + 1)

  // If we rolled into the following month (e.g. 31 → 3rd of month+1) clamp back
  if ((date.getMonth() + 12) % 12 !== (startMonth + 1) % 12) {
    date.setDate(0) // "0" means last day of previous month in JS Date API
  }

  return Timestamp.fromDate(date)
}

// Credit display utilities
export const formatCreditDisplay = creditData => {
  if (!creditData) return '0 credits'

  if (creditData.isUnlimited) {
    return 'Unlimited'
  }

  const monthly = creditData.creditsAvailable || 0
  const topOff = creditData.topOffCreditsAvailable || 0
  const total = monthly + topOff

  if (topOff > 0) {
    return `${total} credits (${monthly} monthly + ${topOff} top-off)`
  }

  return `${total} credits`
}

export const getCreditBreakdown = creditData => {
  if (!creditData) {
    return {
      total: 0,
      monthly: 0,
      topOff: 0,
      isUnlimited: false,
    }
  }

  const monthly = creditData.creditsAvailable || 0
  const topOff = creditData.topOffCreditsAvailable || 0

  return {
    total: monthly + topOff,
    monthly,
    topOff,
    topOffTotal: creditData.topOffCreditsTotal || 0,
    isUnlimited: creditData.isUnlimited || false,
  }
}

/**
 * Example usage of enhanced credit system:
 *
 * ```jsx
 * import { useUserCredits, formatCreditDisplay, getCreditBreakdown } from '@/hooks/useCredits'
 *
 * function CreditDisplayComponent() {
 *   const { data: credits, isLoading } = useUserCredits()
 *
 *   if (isLoading) return <div>Loading...</div>
 *
 *   const breakdown = getCreditBreakdown(credits)
 *   const displayText = formatCreditDisplay(credits)
 *
 *   return (
 *     <div>
 *       <h3>{displayText}</h3>
 *       {!breakdown.isUnlimited && (
 *         <div>
 *           <p>Monthly: {breakdown.monthly} credits</p>
 *           <p>Top-off: {breakdown.topOff} credits</p>
 *           <p>Total purchased top-off: {breakdown.topOffTotal} credits</p>
 *         </div>
 *       )}
 *     </div>
 *   )
 * }
 * ```
 */

// Fetch credits data
const fetchCredits = async () => {
  const contractsCollection = collection(db, 'creditContracts')
  const contractsSnapshot = await getDocs(contractsCollection)

  const creditsData = contractsSnapshot.docs.map(contractDoc => {
    const contractData = contractDoc.data()

    return {
      id: contractDoc.id,
      ...contractData,
    }
  })

  return creditsData
}

// Fetch individual user credits
const fetchUserCredits = async ({ uid, email }) => {
  if (!uid) {
    throw new Error('User ID is required')
  }

  const contractDocRef = doc(db, 'creditContracts', uid)
  const contractSnap = await getDoc(contractDocRef)

  if (!contractSnap.exists()) {
    const now = Timestamp.now()
    const newContract = {
      userId: uid,
      email: email,
      effectivePlanId: 'free_tier',
      status: 'active',
      isUnlimited: false,
      creditsAvailable: 100,
      creditsAllocatedThisPeriod: 100,
      topOffCreditsAvailable: 0,
      topOffCreditsTotal: 0,
      allocationSource: 'free_tier',
      sourceDetails: {},
      periodStart: now,
      periodEnd: addOneMonth(now),
      planTermEndTimestamp: null,
      ownerType: 'individual',
      createdAt: now,
      lastUpdatedAt: now,
    }

    try {
      await setDoc(contractDocRef, newContract, { merge: false })

      return {
        id: uid,
        ...newContract,
        totalCreditsAvailable: 100,
      }
    } catch (error) {
      return {
        id: uid,
        userId: uid,
        email: email,
        effectivePlanId: 'free_tier',
        status: 'inactive',
        isUnlimited: false,
        creditsAvailable: 100,
        creditsAllocatedThisPeriod: 100,
        topOffCreditsAvailable: 0,
        topOffCreditsTotal: 0,
        allocationSource: 'free_tier',
        sourceDetails: {},
        periodStart: null,
        periodEnd: null,
        planTermEndTimestamp: null,
        totalCreditsAvailable: 100,
        ownerType: 'individual',
        createdAt: new Date().toISOString(),
        lastUpdatedAt: new Date().toISOString(),
      }
    }
  }

  const contractData = contractSnap.data()

  // Calculate total available credits using existing fields
  const monthlyCredits = contractData.creditsAvailable || 0
  const topOffCredits = contractData.topOffCreditsAvailable || 0
  const totalCredits = monthlyCredits + topOffCredits

  return {
    id: contractSnap.id,
    ...contractData,
    // Add computed total for convenience
    totalCreditsAvailable: totalCredits,
  }
}

export const useCredits = () => {
  return useQuery({
    queryKey: [CREDITS_QUERY_KEY],
    queryFn: fetchCredits,
    refetchInterval: 1000 * 60 * 5,
  })
}

export const useUserCredits = () => {
  const { email, uid } = useAuthUser()

  return useQuery({
    queryKey: [USER_CREDITS_QUERY_KEY, uid],
    queryFn: () => fetchUserCredits({ uid, email }),
    enabled: !!uid,
    refetchInterval: 1000 * 60 * 5,
  })
}

// Consume credits function using existing structure
const consumeCredits = async ({
  activityId,
  activityCredits,
  numClassrooms,
  uid,
  selectedClassrooms,
  hasUnlimitedAccess,
}) => {
  try {
    const contractRef = doc(db, 'creditContracts', uid)
    const contractDoc = await getDoc(contractRef)

    if (!contractDoc.exists()) {
      throw new Error('Credit contract not found for this user')
    }

    const contract = contractDoc.data()
    const now = Timestamp.now()

    // Apply teacher_pro pass/fail exception to credit calculation
    let totalRequired = activityCredits * numClassrooms

    // Get current credit balances using existing fields
    const monthlyAvailable = contract.creditsAvailable || 0
    const topOffAvailable = contract.topOffCreditsAvailable || 0
    const totalAvailable = monthlyAvailable + topOffAvailable

    // Calculate credit consumption (monthly first, then top-off)
    let monthlyUsed = 0
    let topOffUsed = 0
    let remaining = totalRequired

    // Skip deduction for unlimited users
    if (hasUnlimitedAccess) {
      // Create transaction for tracking but don't deduct
      const transactionRef = await addDoc(collection(db, 'creditContracts', uid, 'creditTransactions'), {
        userId: uid,
        amount: -totalRequired,
        activityId,
        classroomsAssigned: numClassrooms,
        assignedClassroomIds: selectedClassrooms || [],
        reason: 'assignment',
        timestamp: now,
        creditsPerClassroom: activityCredits,
        isUnlimited: true,
      })

      return {
        success: true,
        consumed: totalRequired,
        remaining: null,
        uid,
        transactionId: transactionRef.id,
        isUnlimited: true,
      }
    }

    // Check for sufficient balance
    if (totalAvailable < totalRequired) {
      throw new Error('Insufficient credits')
    }

    // Use monthly credits first
    if (monthlyAvailable > 0 && remaining > 0) {
      monthlyUsed = Math.min(monthlyAvailable, remaining)
      remaining -= monthlyUsed
    }

    // Use top-off credits for remainder
    if (remaining > 0) {
      topOffUsed = remaining
    }

    // Create transaction
    const transactionRef = await addDoc(collection(db, 'creditContracts', uid, 'creditTransactions'), {
      userId: uid,
      amount: -totalRequired,
      activityId,
      classroomsAssigned: numClassrooms,
      assignedClassroomIds: selectedClassrooms || [],
      reason: 'assignment',
      timestamp: now,
      creditsPerClassroom: activityCredits,
      isUnlimited: false,
    })

    // Update credit balances using existing fields
    await updateDoc(contractRef, {
      creditsAvailable: monthlyAvailable - monthlyUsed,
      topOffCreditsAvailable: topOffAvailable - topOffUsed,
      lastUpdatedAt: now,
    })

    return {
      success: true,
      consumed: totalRequired,
      remaining: totalAvailable - totalRequired,
      uid,
      transactionId: transactionRef.id,
      isUnlimited: false,
    }
  } catch (error) {
    console.error('Error consuming credits:', error)
    throw error
  }
}

export const useConsumeCredits = () => {
  const { uid } = useAuthUser()
  const queryClient = useQueryClient()
  const { data: organizationAccess } = useOrganizationAccess()
  const hasUnlimitedAccess = organizationAccess?.hasUnlimitedAccess

  const mutation = useMutation({
    mutationFn: params => consumeCredits({ ...params, uid, hasUnlimitedAccess }),
    onSuccess: () => {
      // Invalidate and refetch credits after successful consumption
      queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY] })
      // Also invalidate the specific user's credits using uid
      queryClient.invalidateQueries({ queryKey: [USER_CREDITS_QUERY_KEY, uid] })
      // Force a refetch of the user's credits
      queryClient.refetchQueries({ queryKey: [USER_CREDITS_QUERY_KEY, uid] })
      // Invalidate other credit-related queries
      queryClient.invalidateQueries({ queryKey: ['userCredits'] })
      queryClient.invalidateQueries({ queryKey: ['creditContract'] })
      queryClient.invalidateQueries({ queryKey: ['creditTransactions'] })
    },
    onError: error => {
      console.error('Error consuming credits:', error)
    },
  })

  return {
    consumeCredits: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error?.message,
  }
}
